// Static scene metadata loaded from scene.json. Distinct from runtime params
// (which live in the reactive store). Holds coord system, axis config,
// fieldline defaults, lighting, dataset map, etc.

import * as THREE from 'three';
import { SCENE_DEFAULTS } from '../../config/defaults';
import type { Vec3Obj } from '../../store/schema';

export interface FieldlineConfig {
    color: number;
    coneLength: number;
    coneRadius: number;
    coneColor: number | string | null;
    opacity: number;
    coneOpacity: number;
}

export interface SelectionMeta {
    sphereRadius: number;
    position: THREE.Vector3;
    projectionColor: string | number | null;
    projectionOpacity: number | null;
}

export interface AxisVisualConfig {
    labelSize: number;
    gridLabelSize: number;
    gridMinorOpacity: number | null;
    gridMajorOpacity: number | null;
    gridMinorFoot: number;
    gridMajorFoot: number;
    labelExtraPadding: number;
}

export type UiDriver = 'panel' | 'headless';

export interface UiMeta {
    showThemeSection: boolean;
    showSelectionSection: boolean;
    driver: UiDriver;
}

export interface LightingChannel {
    color?: string;
    intensity?: number;
    position?: Vec3Obj;
}

export interface LightingConfig {
    ambient?: { color?: string; intensity?: number };
    sunLight?: LightingChannel;
    fillLight?: LightingChannel;
}

export interface DatasetEntry {
    url?: string;
    label?: string;
    component?: string;
    [key: string]: unknown;
}

export interface SceneMeta {
    coordinateSystem: string;
    units: string;
    cameraGSM: THREE.Vector3 | null;
    axisConfigPresent: boolean;
    showCoordAxes: boolean;
    axesVectorsConfig: Record<string, unknown> | null;
    debugEnabled: boolean;
    developerMode: boolean;
    showFps: boolean;
    helpStart: boolean | Record<string, unknown>;
    lighting: LightingConfig | null;
    fieldlineConfig: FieldlineConfig;
    selection: SelectionMeta;
    themeUrl: string | null;
    defaultDataset: string | null;
    datasetMap: Record<string, DatasetEntry>;
    axis: AxisVisualConfig;
    ui: UiMeta;
}

export function createSceneMeta(): SceneMeta {
    return {
        coordinateSystem: 'GSM',
        units: 'Re',
        cameraGSM: new THREE.Vector3(SCENE_DEFAULTS.cameraPhys.x, SCENE_DEFAULTS.cameraPhys.y, SCENE_DEFAULTS.cameraPhys.z),
        axisConfigPresent: false,
        showCoordAxes: false,
        axesVectorsConfig: null,
        debugEnabled: false,
        developerMode: false,
        showFps: SCENE_DEFAULTS.showFps,
        helpStart: false,
        lighting: null,
        fieldlineConfig: {
            color: 0xffcc66,
            coneLength: 0.25,
            coneRadius: 0.07,
            coneColor: null,
            opacity: 1.0,
            coneOpacity: 0.85,
        },
        selection: {
            sphereRadius: 0.15,
            position: new THREE.Vector3(SCENE_DEFAULTS.selectionPos.x, SCENE_DEFAULTS.selectionPos.y, SCENE_DEFAULTS.selectionPos.z),
            projectionColor: null,
            projectionOpacity: null,
        },
        themeUrl: null,
        defaultDataset: null,
        datasetMap: {},
        axis: {
            labelSize: 1.0,
            gridLabelSize: 1.2,
            gridMinorOpacity: null,
            gridMajorOpacity: null,
            gridMinorFoot: 0,
            gridMajorFoot: 0,
            labelExtraPadding: 0,
        },
        ui: {
            showThemeSection: true,
            showSelectionSection: true,
            driver: 'panel',
        },
    };
}
